/**
 * Author: Fleming Shi
 * Project: DAL (Digital Abstraction Layer)
 * Date: 2026-04-30
 * Description: Returning-user sign-in. Accepts a 6-digit code — either from
 *              an authenticator app (if enrolled) or from a fresh email-OTP
 *              ("Email me a code" button). Backend tries TOTP first, falls
 *              back to email-OTP transparently.
 */

import React, { useState } from 'react'
import { Link, useNavigate, useSearchParams } from 'react-router-dom'
import { api } from '@services/api'
import { toast } from 'react-toastify'

const ApiAccessLogin: React.FC = () => {
  const navigate = useNavigate()
  const [params] = useSearchParams()
  const [email, setEmail] = useState(params.get('email') ?? '')
  const [code, setCode] = useState('')
  const [submitting, setSubmitting] = useState(false)
  const [requestingOtp, setRequestingOtp] = useState(false)

  const handleSubmit = async (e: React.FormEvent) => {
    e.preventDefault()
    if (!/^\d{6}$/.test(code)) {
      toast.error('Enter the 6-digit code from your email or authenticator app')
      return
    }
    setSubmitting(true)
    try {
      await api.accessRequestRedeem(email.trim(), code.trim())
      // Successful sign-in: send the user to the welcome page so they get the
      // optional authenticator-enrollment offer, then on to the docs.
      navigate('/api-access/welcome')
    } catch (err: unknown) {
      const msg =
        (err as { response?: { data?: { error?: string } } })?.response?.data?.error ?? 'Invalid email or code'
      toast.error(msg)
    } finally {
      setSubmitting(false)
    }
  }

  const handleEmailOtp = async () => {
    const trimmedEmail = email.trim()
    if (!trimmedEmail) {
      toast.error('Enter your email first')
      return
    }
    setRequestingOtp(true)
    try {
      await api.accessRequestRequestOtp(trimmedEmail)
      toast.success('Code sent. Check your inbox.')
    } catch (err: unknown) {
      const status = (err as { response?: { status?: number } })?.response?.status
      if (status === 404) {
        toast.info('No approved access found for that email — let us get you registered.')
        navigate(`/api-access/register?email=${encodeURIComponent(trimmedEmail)}`)
        return
      }
      const msg =
        (err as { response?: { data?: { error?: string } } })?.response?.data?.error ??
        'Could not request a code, try again in a minute'
      toast.error(msg)
    } finally {
      setRequestingOtp(false)
    }
  }

  return (
    <div className="mx-auto max-w-md py-16">
      <h1 className="text-2xl font-bold text-gray-900 dark:text-gray-100">Sign in to API docs</h1>
      <p className="mt-2 text-sm text-gray-600 dark:text-gray-400">
        Enter the 6-digit code from your authenticator app, or click <em>Email me a code</em> to get one by email.
      </p>

      <form onSubmit={handleSubmit} className="mt-6 space-y-4">
        <div>
          <label className="block text-sm font-medium text-gray-700 dark:text-gray-300">Email</label>
          <input
            type="email"
            value={email}
            onChange={(e) => setEmail(e.target.value)}
            required
            className="mt-1 block w-full rounded-md border-gray-300 shadow-sm focus:border-indigo-500 focus:ring-indigo-500 sm:text-sm dark:border-gray-600 dark:shadow-none"
          />
        </div>
        <div>
          <label className="block text-sm font-medium text-gray-700 dark:text-gray-300">6-digit code</label>
          <input
            inputMode="numeric"
            autoComplete="one-time-code"
            maxLength={6}
            value={code}
            onChange={(e) => setCode(e.target.value.replace(/\D/g, ''))}
            required
            placeholder="000000"
            className="mt-1 block w-40 rounded-md border-gray-300 text-center font-mono text-lg tracking-widest shadow-sm focus:border-indigo-500 focus:ring-indigo-500 dark:border-gray-600 dark:shadow-none"
          />
        </div>
        <button
          type="submit"
          disabled={submitting}
          className="inline-flex w-full items-center justify-center rounded-md border border-transparent bg-indigo-600 px-4 py-2 text-sm font-medium text-white shadow-sm hover:bg-indigo-700 disabled:opacity-50 dark:shadow-none"
        >
          {submitting ? 'Signing in…' : 'Sign in'}
        </button>
      </form>

      <div className="mt-4 text-center text-sm">
        <button
          type="button"
          onClick={handleEmailOtp}
          disabled={requestingOtp}
          className="text-indigo-600 underline disabled:opacity-50"
        >
          {requestingOtp ? 'Sending…' : 'Email me a code'}
        </button>
      </div>

      <p className="mt-8 text-sm text-gray-500 dark:text-gray-400">
        Don&apos;t have access yet?{' '}
        <Link className="text-indigo-600 underline" to="/api-access/register">
          Request access
        </Link>
        .
      </p>
    </div>
  )
}

export default ApiAccessLogin
